import { useMemo } from 'react';

import type { TranscriptElement } from '@src/types.ts/transcript';
import type { HighlightRange } from '@src/utility/transcriptQuoteUtils';

interface SavedSegmentVisibilityOptions {
  /** The Highlights filter is in Show Only mode (vs. Show All). */
  showOnly: boolean;
  /** The host's own filter (Story Topics) is in Show Only mode. */
  hostShowOnly?: boolean;
  /** Active source-index ranges, already filtered by the header controls. */
  ranges: HighlightRange[];
  /** Source elements; ranges index into this array. */
  transcriptElements: TranscriptElement[] | undefined;
  /** An incoming filter (the topic filter); `undefined` means unfiltered. */
  visibleParagraphIds?: ReadonlySet<string>;
}

interface SavedSegmentVisibility {
  /** Paragraphs the highlights filter alone leaves visible. */
  visibleParagraphIds: ReadonlySet<string> | undefined;
  /** Element indices inside the active ranges, for the word-level trim. */
  visibleElementIndices: ReadonlySet<number> | undefined;
  /** The highlights filter intersected with the incoming one. */
  effectiveVisibleParagraphIds: ReadonlySet<string> | undefined;
  /** Some filter is in Show Only — narrowing the transcript, or armed to. */
  showOnlyActive: boolean;
}

/**
 * Resolve which paragraphs and words a Show Only filter leaves visible.
 * Extracted from `Transcript.tsx` per CLAUDE.md → "Architecture Guardrails"
 * rule 5 (PLAT-11182).
 *
 * The visible sets go `undefined` whenever a filter has nothing to show, so
 * everything gating OTHER controls — the menus' mutual block, the Quotes/Clips
 * pills, the marks-clearing effect — keys on the MODES (`showOnly`,
 * `hostShowOnly`) instead: a guard that lapses lets both filters into Show
 * Only at once, which is the state the guard exists to prevent.
 */
export function useSavedSegmentVisibility({
  showOnly,
  hostShowOnly = false,
  ranges,
  transcriptElements,
  visibleParagraphIds,
}: SavedSegmentVisibilityOptions): SavedSegmentVisibility {
  // Show-only mode hides every paragraph that isn't part of an active segment,
  // so the transcript shows only the highlighted sections. "Show All" (empty
  // selection) keeps all segments active via the ranges passed in, so it
  // restricts to every saved segment rather than disabling the filter.
  // Returns undefined (no filtering) when not in show-only mode or when there
  // are no ranges to show (which would otherwise hide the whole transcript).
  const savedSegmentVisibleParagraphIds = useMemo(() => {
    if (!showOnly || ranges.length === 0) return undefined;
    const ids = new Set<string>();
    for (const range of ranges) {
      for (let i = range.start; i <= range.end; i++) {
        const pid = transcriptElements?.[i]?.paragraph_id;
        if (pid) ids.add(pid);
      }
    }
    return ids;
  }, [showOnly, ranges, transcriptElements]);

  // In highlights show-only mode, trim the visible paragraphs down to just the
  // highlighted words (the element indices inside the active segment ranges)
  // instead of showing the full paragraph. Undefined outside show-only mode so
  // every word renders normally. The topic filter has no equivalent — it stays
  // paragraph-level (see `savedSegmentVisibleParagraphIds`).
  const visibleElementIndices = useMemo(() => {
    if (!showOnly || ranges.length === 0) return undefined;
    const indices = new Set<number>();
    for (const range of ranges) {
      for (let i = range.start; i <= range.end; i++) {
        indices.add(i);
      }
    }
    return indices;
  }, [showOnly, ranges]);

  // Combine the saved-segment show-only set with any incoming visibility filter
  // (e.g. the topic filter). `undefined` means unfiltered; when both are
  // present, only paragraphs in both stay visible.
  const effectiveVisibleParagraphIds = useMemo(() => {
    if (!savedSegmentVisibleParagraphIds) return visibleParagraphIds;
    if (!visibleParagraphIds) return savedSegmentVisibleParagraphIds;
    return new Set(
      [...savedSegmentVisibleParagraphIds].filter(id =>
        visibleParagraphIds.has(id),
      ),
    );
  }, [savedSegmentVisibleParagraphIds, visibleParagraphIds]);

  return {
    visibleParagraphIds: savedSegmentVisibleParagraphIds,
    visibleElementIndices,
    effectiveVisibleParagraphIds,
    // PLAT-11039: Show Only hides non-matching paragraphs, so quote/clip marks
    // would sit on unrendered text and their nav would step to invisible
    // matches. Turn them off and inert the pills. Mode-keyed (PLAT-11182): the
    // pills go inert with the other menu, not only once something is hidden.
    showOnlyActive: showOnly || hostShowOnly || !!effectiveVisibleParagraphIds,
  };
}
